import React, { useEffect, useMemo, useRef, useState } from "react";
import { Field, reduxForm } from "redux-form";
import { CircularProgress } from "@mui/material";
import { ContentState, EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

import emailEditor from "../../../../../../common/EmailEditor";
import styles from "./JobSwmsFormV3PreStart.module.css";
import commonStyles from "../../../../../SwmsMasters/SwmsMastersStyle.module.css";
import JobSwmsV3PreStartQuestionsList from "./JobSwmsV3PreStartQuestionsList";

const getEditorStateFromHtml = (html) => {
  const htmlValue = html ? `${html}` : "";

  if (!htmlValue) {
    return EditorState.createEmpty();
  }

  const parsedHtml = htmlToDraft(htmlValue);
  if (parsedHtml && parsedHtml.contentBlocks) {
    const contentState = ContentState.createFromBlockArray(parsedHtml.contentBlocks);
    return EditorState.createWithContent(contentState);
  }

  return EditorState.createEmpty();
};

const getHtmlFromEditorState = (state) => {
  if (!state) {
    return "";
  }

  const contentState = state.getCurrentContent();
  const plainText = contentState.getPlainText("\n").replace(/\u00a0/g, " ").trim();

  if (!plainText) {
    return "";
  }

  return normalizeEditorHtml(draftToHtml(convertToRaw(contentState)) || "");
};

const normalizeEditorHtml = (html) => {
  const htmlValue = html ? `${html}` : "";
  const normalizedHtml = htmlValue
    .replace(/&nbsp;/gi, " ")
    .replace(/<br\s*\/?>(\n)?/gi, "")
    .replace(/<[^>]+>/g, "")
    .trim();

  if (!normalizedHtml) {
    return "";
  }

  return htmlValue;
};

const getVisibleOrders = (questions = []) => {
  let activeOrder = 0;

  return questions.map((question = {}) => {
    if (question.active) {
      activeOrder += 1;
      return activeOrder;
    }

    return "-";
  });
};

function JobSwmsV3PreStartContainer({
  checklist = {},
  onOpenAddQuestion = () => { },
  onOpenEditQuestion = () => { },
  moveQuestionRow = () => { },
  onAfterDropQuestionRow = () => { },
  onCancelQuestions = () => { },
  onSaveQuestions = () => { },
  onHeadingContentChange = () => { },
  onStaffSignoffHeadingContentChange = () => { },
  hasUnsavedQuestionChanges = false,
  questionInteractionDisabled = false,
  requestInFlight = false,
  initialValues,
  change
}) {
  const questions = useMemo(() => {
    if (!checklist || !checklist.data || !Array.isArray(checklist.data.pre_start_questions)) {
      return [];
    }

    return checklist.data.pre_start_questions;
  }, [checklist]);

  const visibleOrders = useMemo(() => getVisibleOrders(questions), [questions]);
  const initialHeading = initialValues && initialValues.prestart_checklist_heading ? initialValues.prestart_checklist_heading : "";
  const initialStaffSignoffHeading = initialValues && initialValues.prestart_staff_signoff ? initialValues.prestart_staff_signoff : "";
  const [editorState, setEditorState] = useState(() => getEditorStateFromHtml(initialHeading));
  const [staffSignoffEditorState, setStaffSignoffEditorState] = useState(() => getEditorStateFromHtml(initialStaffSignoffHeading));
  const lastLocalHeadingHtmlRef = useRef(normalizeEditorHtml(initialHeading));
  const lastLocalStaffSignoffHtmlRef = useRef(normalizeEditorHtml(initialStaffSignoffHeading));

  useEffect(() => {
    const normalizedInitialHeading = normalizeEditorHtml(initialHeading);

    if (normalizedInitialHeading === lastLocalHeadingHtmlRef.current) {
      return;
    }

    setEditorState(getEditorStateFromHtml(initialHeading));
    lastLocalHeadingHtmlRef.current = normalizedInitialHeading;
  }, [initialHeading]);

  useEffect(() => {
    const normalizedInitialStaffSignoffHeading = normalizeEditorHtml(initialStaffSignoffHeading);

    if (normalizedInitialStaffSignoffHeading === lastLocalStaffSignoffHtmlRef.current) {
      return;
    }

    setStaffSignoffEditorState(getEditorStateFromHtml(initialStaffSignoffHeading));
    lastLocalStaffSignoffHtmlRef.current = normalizedInitialStaffSignoffHeading;
  }, [initialStaffSignoffHeading]);

  const onEditorStateChange = (nextState) => {
    setEditorState(nextState);
    const htmlValue = getHtmlFromEditorState(nextState);
    lastLocalHeadingHtmlRef.current = normalizeEditorHtml(htmlValue);
    change("prestart_checklist_heading", htmlValue);
    onHeadingContentChange(htmlValue ? `${htmlValue}` : "");
  };

  const onStaffSignoffEditorStateChange = (nextState) => {
    setStaffSignoffEditorState(nextState);
    const htmlValue = getHtmlFromEditorState(nextState);
    lastLocalStaffSignoffHtmlRef.current = normalizeEditorHtml(htmlValue);
    change("prestart_staff_signoff", htmlValue);
    onStaffSignoffHeadingContentChange(htmlValue ? `${htmlValue}` : "");
  };

  return (
    <div className={styles.PreStartContainer}>
      <div className={styles.PreStartHeaderRow}>
        <div>
          <div className={styles.PreStartTitle}>Pre-Start Checklist</div>
          <div className={styles.PreStartSubtitle}>Manage checklist visibility and view the current question set.</div>
        </div>
        <div className={styles.PreStartActionButtons}>
          <button
            className={commonStyles.PrimaryButton}
            type="button"
            onClick={onOpenAddQuestion}
            disabled={questionInteractionDisabled}
          >
            Add Question
          </button>
        </div>
      </div>

      <div className={styles.PreStartHeadingWrap}>
        <div className={commonStyles.Label}>Checklist Questions Heading</div>
        <div className="swmsv2-custom-draft-editor">
          <Field
            name="prestart_checklist_heading"
            component={emailEditor}
            disabled={questionInteractionDisabled}
            editorState={editorState}
            onEditorStateChange={onEditorStateChange}
            otherProps={{
              editorStyle: {
                padding: "0px 4px",
                minHeight: "110px"
              }
            }}
          />
        </div>
      </div>

      <div className={styles.PreStartQuestionsWrap}>
        {questions.length > 0 ? (
          <JobSwmsV3PreStartQuestionsList
            questions={questions}
            visibleOrders={visibleOrders}
            reorderDisabled={questionInteractionDisabled}
            moveQuestionRow={moveQuestionRow}
            onAfterDropQuestionRow={onAfterDropQuestionRow}
            onOpenEditQuestion={onOpenEditQuestion}
          />
        ) : (
          <div className={styles.PreStartEmptyState}>No questions yet.</div>
        )}
      </div>

      <div className={styles.PreStartHeadingWrap}>
        <div className={commonStyles.Label}>Pre-Start Sign Off Heading</div>
        <div className="swmsv2-custom-draft-editor">
          <Field
            name="prestart_staff_signoff"
            component={emailEditor}
            disabled={questionInteractionDisabled}
            editorState={staffSignoffEditorState}
            onEditorStateChange={onStaffSignoffEditorStateChange}
            otherProps={{
              editorStyle: {
                padding: "0px 4px",
                minHeight: "110px"
              }
            }}
          />
        </div>
      </div>
        
    </div>
  );
}

export default reduxForm({
  form: "JobSwmsV3PreStartContainerForm",
  destroyOnUnmount: true,
  enableReinitialize: true
})(JobSwmsV3PreStartContainer);
